import {
  Box,
  Button,
  Collapse,
  Divider,
  Icon,
  OsStack,
  Typography,
} from "@outschool/backpack";
import { TextMimeType } from "@outschool/gql-frontend-generated";
import { faClock, faQuestionCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  AttachmentGallery,
  TextWithLinks,
} from "@outschool/ui-components-shared";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { RichTextView } from "@outschool/ui-rich-text-editor";
import React, { FC, useCallback, useRef, useState } from "react";
import { Player } from "video.js";

import { useFormatDuration } from "../../../hooks/useFormatDuration";
import { POST_CONTENT_TRUNCATE_LENGTH } from "../../../lib/constants";
import { toEmbedUrl } from "../../../lib/videoEmbed";
import OutschoolVideoJsAdapter from "../../VideoJs/OutschoolVideoJsAdapter";
import { LessonItemType } from "../lessons/types";
import { MessageContent, Sender } from "../types";

const VIDEO_TIME_UPDATE_INTERVAL_MS = 5000;

interface VideoWithEventsProps {
  src: string;
  posterUrl?: string | null;
  onVideoTimeUpdate?: (currentTime: number, duration: number) => void;
  onVideoEnded?: () => void;
}

function VideoWithEvents({
  src,
  posterUrl,
  onVideoTimeUpdate,
  onVideoEnded,
}: VideoWithEventsProps) {
  const callbacksRef = useRef({ onVideoTimeUpdate, onVideoEnded });
  callbacksRef.current = { onVideoTimeUpdate, onVideoEnded };
  const lastEmitRef = useRef(0);

  const handlePlayerReady = useCallback((player: Player) => {
    player.on("timeupdate", () => {
      const now = Date.now();
      if (now - lastEmitRef.current < VIDEO_TIME_UPDATE_INTERVAL_MS) {
        return;
      }
      lastEmitRef.current = now;

      const currentTime = player.currentTime?.() ?? 0;
      const duration = player.duration?.() ?? 0;
      if (duration > 0) {
        callbacksRef.current.onVideoTimeUpdate?.(currentTime, duration);
      }
    });

    player.on("ended", () => {
      callbacksRef.current.onVideoEnded?.();
    });
  }, []);

  return (
    <Box sx={{ borderRadius: 16, overflow: "hidden" }}>
      <OutschoolVideoJsAdapter
        src={src}
        posterUrl={posterUrl}
        onPlayerReady={handlePlayerReady}
      />
    </Box>
  );
}

export interface ClassPostContentProps {
  sender: Sender;
  messageContent: MessageContent;
  trackingLabel?: string;
  trackingMetadata?: TrackingProperties;
  allowRichText?: boolean;
  /**
   * Height of the collapsed content in pixels.
   * @default 150
   */
  collapseSize?: number;
  estimatedDuration?: number | null;
  postType?: LessonItemType;
  /** When true, content is never collapsed and "Show more" is hidden. */
  disableCollapse?: boolean;
  onVideoTimeUpdate?: (currentTime: number, duration: number) => void;
  onVideoEnded?: () => void;
}

const ClassPostContent: FC<ClassPostContentProps> = ({
  sender,
  messageContent,
  trackingLabel,
  trackingMetadata,
  allowRichText = false,
  collapseSize = 150,
  estimatedDuration,
  postType,
  disableCollapse = false,
  onVideoTimeUpdate,
  onVideoEnded,
}) => {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassPost\\ClassPostContent",
  );
  const formatDuration = useFormatDuration();
  const [userExpanded, setUserExpanded] = useState(false);

  const [contentRef, setContentRef] = React.useState<
    HTMLDivElement | HTMLPreElement | null
  >(null);
  const contentSize = contentRef?.scrollHeight;

  const textLength = messageContent.text?.length ?? 0;
  const htmlTextLength = messageContent.htmlText?.length ?? 0;
  const contentLength = Math.max(textLength, htmlTextLength);
  const isLongContent =
    !disableCollapse &&
    contentLength >= POST_CONTENT_TRUNCATE_LENGTH &&
    (!contentSize || contentSize > collapseSize);
  const expanded = userExpanded || !isLongContent;
  const showExpandButton = !expanded;

  const showEstimatedDuration =
    estimatedDuration != null && estimatedDuration > 0;

  const uploadedVideoSrc =
    messageContent.video?.fileUrl ?? messageContent.video?.file?.url ?? null;
  const externalVideoUrl = messageContent.externalVideoUrl ?? null;
  const embedUrl = externalVideoUrl ? toEmbedUrl(externalVideoUrl) : null;

  const showEmbed = !uploadedVideoSrc && !!embedUrl;
  const showVideo = !!uploadedVideoSrc || (!showEmbed && !!externalVideoUrl);
  const videoSrc = uploadedVideoSrc ?? externalVideoUrl ?? "";

  const hasText = !!(
    messageContent.text?.trim() || messageContent.htmlText?.trim()
  );
  const hasImages = (messageContent.imageAttachments ?? []).length > 0;
  const hasContent = showVideo || showEmbed || hasText || hasImages;

  if (!hasContent) {
    return null;
  }

  return (
    <Box
      data-test-id="ClassPostContent"
      sx={{
        backgroundColor: "common.white",
        borderRadius: 16,
        paddingX: 32,
        paddingY: 20,
      }}
    >
      <OsStack spacing={5}>
        {(postType === "assignment" || showEstimatedDuration) && (
          <>
            <OsStack direction="row" spacing={2} alignItems="center">
              <Icon
                icon={postType === "assignment" ? faQuestionCircle : faClock}
                sx={{ fontSize: 20, color: "neutral.800" }}
              />
              <Typography variant="subtitle1">
                {postType === "assignment"
                  ? t("Assignment instructions")
                  : formatDuration(estimatedDuration!, showVideo || showEmbed)}
              </Typography>
            </OsStack>
            <Divider sx={{ borderColor: "neutral.100" }} />
          </>
        )}
        {showVideo && (
          <VideoWithEvents
            src={videoSrc}
            posterUrl={messageContent.video?.videoThumbnailUrl}
            onVideoTimeUpdate={onVideoTimeUpdate}
            onVideoEnded={onVideoEnded}
          />
        )}
        {showEmbed && (
          <Box
            component="iframe"
            src={embedUrl}
            // eslint-disable-next-line i18next/no-literal-string
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              border: 0,
              borderRadius: 16,
            }}
          />
        )}
        <Box
          sx={{
            marginBottom: 40,
            position: "relative",
          }}
        >
          <Collapse
            in={expanded}
            collapsedSize={
              contentSize == null
                ? 0
                : contentSize < collapseSize
                ? contentSize
                : collapseSize
            }
          >
            {allowRichText &&
            messageContent.originalMimeType === TextMimeType.TextMarkdown ? (
              <RichTextView
                content={messageContent.htmlText}
                allowLinks={true}
                maxLines={Infinity}
                allowEmails={false}
                ref={setContentRef}
              />
            ) : (
              <Box
                component="pre"
                sx={{ wordBreak: "break-word", margin: 0 }}
                ref={setContentRef}
              >
                <TextWithLinks
                  clickableLinks={true}
                  text={messageContent.text?.trim() ?? ""}
                  maxTextLength={Infinity}
                  hideEmails={true}
                />
              </Box>
            )}
          </Collapse>
          {showExpandButton && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                position: "absolute",
                bottom: -24,
              }}
            >
              <Typography
                variant="subtitle1"
                color="textSecondary"
                sx={{ userSelect: "none" }}
              >
                ⋯
              </Typography>
              <Button
                onClick={() => setUserExpanded(true)}
                variant="link"
                size="small"
              >
                {t`Show more`}
              </Button>
            </Box>
          )}
        </Box>
      </OsStack>
      {(messageContent.imageAttachments ?? []).length > 0 && (
        <AttachmentGallery
          attachments={messageContent.imageAttachments}
          trackingLabel={trackingLabel}
          trackingMetadata={trackingMetadata}
          senderName={sender.name}
          isPostOwner={false}
        />
      )}
    </Box>
  );
};

export default ClassPostContent;
